import React, { FC, useEffect, useState, ReactNode } from "react";
import { useSelector } from "react-redux";
import { useVerification } from "../../common/hooks/useVerification";
import VerificationRequired from "../../pages/common/VerificationRequired/VerificationRequired";
import { toast } from "react-toastify";

interface EmailAccessWrapperProps {
  children: ReactNode;
  title?: string;
  message?: string;
  buttonText?: string;
  redirectPath?: string;
  onVerifyClick?: () => void;
  showVerifyButton?: boolean;
  fallbackComponent?: ReactNode;
}

const EmailAccessWrapper: FC<EmailAccessWrapperProps> = ({
  children,
  title = "Email Required",
  message = "Please set up your email address in Account Settings before accessing this page.",
  buttonText = "Go to Account Settings",
  redirectPath = "/mysettings",
  onVerifyClick,
  showVerifyButton = true,
  fallbackComponent,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasEmailAccess, setHasEmailAccess] = useState<boolean>(false);
  const { verifyAndCheckAccess, isVerified } = useVerification();

  const _account = useSelector((e: Object) => (e as any)?.common?.account);

  const checkAccess = async () => {
    try {
      setIsLoading(true);
      const hasAccess = await verifyAndCheckAccess();
      setHasEmailAccess(hasAccess);
    } catch (error) {
      console.error("Verification error:", error);
      toast.error("Failed to verify access");
      setHasEmailAccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (_account?.message && _account?.signature) {
      if (isVerified) {
        setHasEmailAccess(true);
        setIsLoading(false);
      } else {
        checkAccess();
      }
    } else {
      setIsLoading(false);
      setHasEmailAccess(false);
    }
  }, [_account, isVerified]);

  // Loading state
  if (isLoading) {
    return (
      <div className="bs-card p-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }


  if (!hasEmailAccess) {
    if (fallbackComponent) {
      return <>{fallbackComponent}</>;
    }

    return (
      <VerificationRequired
        title={title}
        message={message}
        buttonText={buttonText}
        redirectPath={redirectPath}
        onVerifyClick={onVerifyClick}
        showVerifyButton={showVerifyButton}
      />
    );
  }


  return <>{children}</>;
};

export default EmailAccessWrapper;
